/**
 * Per-frame context shared by every layer and pass: smoothed audio features, the (predicted)
 * transport, mood, and the shared data textures. Built once per frame by the Renderer.
 */
import { FRAME_LENGTH, NUM_BANDS } from '../../shared/audioFrame';
import { uniform, type GL, type Program } from './gl/gl';

export const UNIT_SPECTRUM = 0;
export const UNIT_WAVEFORM = 1;
export const UNIT_PALETTE = 2;
export const UNIT_SPECTROGRAM = 3;
/** First texture unit free for pass inputs. */
export const UNIT_INPUT = 4;

export interface FrameContext {
  gl: GL;
  vao: WebGLVertexArrayObject;
  /** Render size (scaled by the governor). */
  width: number;
  height: number;
  time: number;
  /** Continuous beat count (beat index + phase), for beat-synced oscillators. */
  beats: number;
  dt: number;
  frame: number;
  bands: Float32Array;
  bass: number;
  mid: number;
  treble: number;
  level: number;
  centroid: number;
  onset: number;
  beatPhase: number;
  barPhase: number;
  phrasePhase: number;
  beatInBar: number;
  pulse: number;
  bpm: number;
  confidence: number;
  energy: number;
  buildup: number;
  beatReact: number;
  /** Melody pitch (fractional MIDI note, held while unsure), its confidence, and chroma. */
  pitch: number;
  pitchConf: number;
  chroma: Float32Array;
  /** Recent melody, newest first: (pitch, confidence) pairs every PITCH_HISTORY_DT seconds. */
  pitchHist: Float32Array;
  /** Governor quality tier: 0 = full, higher = cheaper. */
  tier: number;
  spectrumTex: WebGLTexture;
  waveformTex: WebGLTexture;
  spectrogramTex: WebGLTexture;
  spectrogramRow: number;
  /** The AudioFrame being displayed (for CPU-side consumers such as Butterchurn). */
  frameData: Float32Array;
}

/** Melody history length and spacing: 128 × 25 ms ≈ 3.2 s. */
export const PITCH_HISTORY = 128;
export const PITCH_HISTORY_DT = 0.025;

export function createFrameContext(
  gl: GL,
  vao: WebGLVertexArrayObject,
  textures: {
    spectrum: WebGLTexture;
    waveform: WebGLTexture;
    spectrogram: WebGLTexture;
  },
): FrameContext {
  return {
    gl,
    vao,
    width: 1,
    height: 1,
    time: 0,
    beats: 0,
    dt: 1 / 60,
    frame: 0,
    bands: new Float32Array(NUM_BANDS),
    bass: 0,
    mid: 0,
    treble: 0,
    level: 0,
    centroid: 0,
    onset: 0,
    beatPhase: 0,
    barPhase: 0,
    phrasePhase: 0,
    beatInBar: 0,
    pulse: 0,
    bpm: 120,
    confidence: 0,
    energy: 0,
    pitch: 0,
    pitchConf: 0,
    chroma: new Float32Array(12),
    pitchHist: new Float32Array(PITCH_HISTORY * 2),
    buildup: 0,
    beatReact: 1,
    tier: 0,
    spectrumTex: textures.spectrum,
    waveformTex: textures.waveform,
    spectrogramTex: textures.spectrogram,
    spectrogramRow: 0,
    frameData: new Float32Array(FRAME_LENGTH),
  };
}

/** Set the standard visualization uniforms (see visualizations/prelude.ts). */
export function setCommonUniforms(
  fc: FrameContext,
  p: Program,
  w: number,
  h: number,
  palette: { tex: WebGLTexture; shift: number; cyclic: boolean },
): void {
  const gl = fc.gl;
  gl.uniform2f(uniform(gl, p, 'uResolution'), w, h);
  gl.uniform1f(uniform(gl, p, 'uTime'), fc.time);
  gl.uniform1f(uniform(gl, p, 'uDt'), fc.dt);
  gl.uniform1fv(uniform(gl, p, 'uBands'), fc.bands);
  gl.uniform1f(uniform(gl, p, 'uBass'), fc.bass);
  gl.uniform1f(uniform(gl, p, 'uMid'), fc.mid);
  gl.uniform1f(uniform(gl, p, 'uTreble'), fc.treble);
  gl.uniform1f(uniform(gl, p, 'uLevel'), fc.level);
  gl.uniform1f(uniform(gl, p, 'uCentroid'), fc.centroid);
  gl.uniform1f(uniform(gl, p, 'uOnset'), fc.onset);
  gl.uniform1f(uniform(gl, p, 'uBeatPhase'), fc.beatPhase);
  gl.uniform1f(uniform(gl, p, 'uBarPhase'), fc.barPhase);
  gl.uniform1f(uniform(gl, p, 'uPhrasePhase'), fc.phrasePhase);
  gl.uniform1f(uniform(gl, p, 'uBeats'), fc.beats);
  gl.uniform1f(uniform(gl, p, 'uBeatInBar'), fc.beatInBar);
  gl.uniform1f(uniform(gl, p, 'uBeatPulse'), fc.pulse);
  gl.uniform1f(uniform(gl, p, 'uBpm'), fc.bpm);
  gl.uniform1f(uniform(gl, p, 'uConfidence'), fc.confidence);
  gl.uniform1f(uniform(gl, p, 'uEnergy'), fc.energy);
  gl.uniform1f(uniform(gl, p, 'uBuildup'), fc.buildup);
  gl.uniform1f(uniform(gl, p, 'uPitch'), fc.pitch);
  gl.uniform1f(uniform(gl, p, 'uPitchConf'), fc.pitchConf);
  gl.uniform1fv(uniform(gl, p, 'uChroma'), fc.chroma);
  gl.uniform2fv(uniform(gl, p, 'uPitchHist'), fc.pitchHist);
  gl.uniform1f(uniform(gl, p, 'uIntensity'), 1);
  gl.uniform1f(uniform(gl, p, 'uBeatReact'), fc.beatReact);
  gl.uniform1f(uniform(gl, p, 'uPaletteShift'), palette.shift);
  gl.uniform1f(uniform(gl, p, 'uPaletteCyclic'), palette.cyclic ? 1 : 0);
  gl.uniform1f(uniform(gl, p, 'uSpectrogramRow'), fc.spectrogramRow);
  gl.activeTexture(gl.TEXTURE0 + UNIT_SPECTRUM);
  gl.bindTexture(gl.TEXTURE_2D, fc.spectrumTex);
  gl.uniform1i(uniform(gl, p, 'uSpectrum'), UNIT_SPECTRUM);
  gl.activeTexture(gl.TEXTURE0 + UNIT_WAVEFORM);
  gl.bindTexture(gl.TEXTURE_2D, fc.waveformTex);
  gl.uniform1i(uniform(gl, p, 'uWaveform'), UNIT_WAVEFORM);
  gl.activeTexture(gl.TEXTURE0 + UNIT_PALETTE);
  gl.bindTexture(gl.TEXTURE_2D, palette.tex);
  gl.uniform1i(uniform(gl, p, 'uPalette'), UNIT_PALETTE);
  gl.activeTexture(gl.TEXTURE0 + UNIT_SPECTROGRAM);
  gl.bindTexture(gl.TEXTURE_2D, fc.spectrogramTex);
  gl.uniform1i(uniform(gl, p, 'uSpectrogram'), UNIT_SPECTROGRAM);
}
